
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertTriangle, Loader2, Repeat, Info } from "lucide-react";

interface ConnectivityErrorProps {
  onTestConnection: () => Promise<boolean>;
  onDiagnostics: () => Promise<void>;
  isTestingConnection: boolean;
}

const ConnectivityError = ({ onTestConnection, onDiagnostics, isTestingConnection }: ConnectivityErrorProps) => {
  return (
    <div className="mb-6 p-4 border border-red-200 rounded-md bg-red-50">
      <div className="flex items-start">
        <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
        <div className="w-full">
          <h3 className="font-medium text-red-800 mb-2">Connection Issue Detected</h3>
          <p className="text-sm text-red-700 mb-3">
            We're having trouble connecting to our payment service. This could be due to:
          </p>
          <ul className="list-disc pl-5 text-sm text-red-700 mb-3">
            <li>Network connectivity issues</li>
            <li>Temporary service outage</li>
            <li>Browser security settings blocking the connection</li>
          </ul>
          <div className="flex gap-3 mt-3">
            <Button 
              variant="outline" 
              size="sm" 
              className="border-red-300 text-red-700 hover:bg-red-100" 
              onClick={onTestConnection}
              disabled={isTestingConnection}
            >
              {isTestingConnection ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Repeat className="mr-2 h-4 w-4" />
                  Test Connection
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-red-300 text-red-700 hover:bg-red-100" 
              onClick={onDiagnostics}
              disabled={isTestingConnection}
            >
              {isTestingConnection ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Diagnosing...
                </>
              ) : (
                <>
                  <Info className="mr-2 h-4 w-4" />
                  Advanced Diagnostics
                </>
              )}
            </Button>
          </div>

          <div className="mt-5">
            <Alert variant="default" className="bg-amber-50 border-amber-200">
              <AlertTitle className="text-amber-800 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Troubleshooting Steps
              </AlertTitle>
              <AlertDescription className="text-amber-700">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-sm font-normal">Try with a different browser</AccordionTrigger>
                    <AccordionContent className="text-xs">
                      Some browser extensions or privacy settings might block connections to Edge Functions. Try using a different browser or incognito mode.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-sm font-normal">Check your network settings</AccordionTrigger>
                    <AccordionContent className="text-xs">
                      Corporate networks and VPNs may block connections to Cloud Functions. Try connecting from a different network.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-sm font-normal">Clear browser cache and cookies</AccordionTrigger>
                    <AccordionContent className="text-xs">
                      Cached data might interfere with the connection. Try clearing your browser's cache and cookies.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectivityError;
